/**
 * Regional sale tier — a tiny client store for display + funnel analytics.
 *
 * Source of truth is the server: /api/geo returns `pricingTier`, `pricingCountry`,
 * `pricingSaleEndsAt` (THIS country's deadline as a UTC instant, only while its sale is on) and
 * `pricingSaleTimeZone`. The client never sends a country.
 * - Successful answers are cached for TTL_MS (sessionStorage) — failures are never cached.
 * - The sale is hidden as soon as `pricingSaleEndsAt` passes on the client clock.
 * - Checkout re-quotes server-side and refuses a price the user did not see
 *   (create-checkout 409 price_changed → refreshPricingTier()).
 */
import { setSaleLifetimeSource } from '../constants/pricing';
import { isDiscountTier, tierPriceUsd } from '../constants/regionalPricing';

const SESSION_KEY = 'cfa_pricing_tier_v2';
const GEO_URLS = ['/api/geo', '/.netlify/functions/geo'];
export const TTL_MS = 5 * 60 * 1000;

let state = { value: null, resolved: false };
let inflight = null;
let version = 0;
let endTimer = null;
const listeners = new Set();

function emit() {
  version += 1;
  listeners.forEach((fn) => {
    try {
      fn();
    } catch {
      /* ignore */
    }
  });
}

function scheduleEnd(value) {
  if (endTimer) clearTimeout(endTimer);
  endTimer = null;
  const ms = Date.parse(value?.endsAt || '') - Date.now();
  // setTimeout overflows past ~24.8 days; a one-week sale is well inside that.
  if (Number.isFinite(ms) && ms > 0 && ms < 2 ** 31 - 1) {
    endTimer = setTimeout(emit, ms + 50);
  }
}

function setValue(value) {
  state = { value, resolved: true };
  scheduleEnd(value);
  emit();
}

function readSession() {
  try {
    const raw = window.sessionStorage.getItem(SESSION_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed || !(Date.now() - Number(parsed.at) < TTL_MS)) return undefined;
    return parsed.value ?? null;
  } catch {
    return undefined;
  }
}

function writeSession(value) {
  try {
    window.sessionStorage.setItem(SESSION_KEY, JSON.stringify({ at: Date.now(), value }));
  } catch {
    /* private mode */
  }
}

function fromGeoResponse(data) {
  const tier = String(data?.pricingTier || '').trim();
  const endsAt = String(data?.pricingSaleEndsAt || '').trim();
  if (!isDiscountTier(tier) || !endsAt) return null;
  const timeZone = String(data?.pricingSaleTimeZone || '').trim().slice(0, 64);
  return {
    tier,
    country: String(data?.pricingCountry || '').trim().toUpperCase().slice(0, 2),
    endsAt,
    ...(timeZone ? { timeZone } : {}),
  };
}

/** Returns the server value, or `undefined` on failure (not cached). */
async function fetchTier() {
  if (typeof fetch !== 'function') return undefined;
  for (const url of GEO_URLS) {
    try {
      const res = await fetch(url);
      if (!res.ok) continue;
      const data = await res.json();
      if (!data || typeof data !== 'object') continue;
      return fromGeoResponse(data);
    } catch {
      /* SPA HTML fallback or offline */
    }
  }
  return undefined;
}

/** Load (or reuse a fresh cached) server tier. Resolves to the active value or null. */
export function loadPricingTier({ force = false } = {}) {
  if (!force) {
    const cached = readSession();
    if (cached !== undefined) {
      if (!state.resolved || state.value?.tier !== cached?.tier || state.value?.endsAt !== cached?.endsAt) {
        setValue(cached);
      }
      return Promise.resolve(getActiveSale());
    }
  }
  if (!inflight) {
    inflight = fetchTier()
      .then((value) => {
        if (value === undefined) {
          // Failure: show list now, but do not cache — the next call retries.
          if (!state.resolved) setValue(null);
        } else {
          writeSession(value);
          setValue(value);
        }
        return getActiveSale();
      })
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

/** Drop the cache and re-ask the server (used after a checkout price_changed). */
export function refreshPricingTier() {
  try {
    window.sessionStorage.removeItem(SESSION_KEY);
  } catch {
    /* ignore */
  }
  return loadPricingTier({ force: true });
}

/** Active sale ({ tier, country, endsAt }) or null — null once endsAt has passed. */
export function getActiveSale(now = Date.now()) {
  const value = state.value;
  if (!value || !isDiscountTier(value.tier)) return null;
  const end = Date.parse(value.endsAt || '');
  return Number.isFinite(end) && now < end ? value : null;
}

export function isPricingTierResolved() {
  return state.resolved;
}

export function subscribePricingTier(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function pricingTierVersion() {
  return version;
}

/** Backwards-compatible alias used by funnel tagging. */
export function getCachedPricingTier() {
  return getActiveSale();
}

const TIER_EVENTS = new Set([
  'pricing_view',
  'paywall_view',
  'purchase_click',
  'checkout_started',
  'checkout_paid',
  'checkout_abandon_reason',
]);

/** `{ pricingTier, country }` for funnel events while a sale tier is active; else `{}`. */
export function pricingTierProps(eventName) {
  if (!TIER_EVENTS.has(eventName)) return {};
  const value = getActiveSale();
  if (!value?.tier) return {};
  return { pricingTier: value.tier, country: value.country || '' };
}

// Sale-aware CTA copy in constants/pricing.js (checkoutCta, unlockMocksFoundingCta, …).
setSaleLifetimeSource(() => {
  const sale = getActiveSale();
  const priceUsd = sale ? tierPriceUsd('l1-annual', sale.tier) : null;
  return sale && priceUsd != null ? { priceUsd, ...sale } : null;
});

/** Test helper. */
export function resetPricingTierCache() {
  state = { value: null, resolved: false };
  inflight = null;
  if (endTimer) clearTimeout(endTimer);
  endTimer = null;
  try {
    window.sessionStorage.removeItem(SESSION_KEY);
  } catch {
    /* ignore */
  }
  emit();
}
